import React, { useMemo, useState, useEffect } from "react";
import { Col, Container, Row } from "react-bootstrap";
import {
  AddFormButton,
  ColumnHeading,
  StatusContainer,
} from "../../../shared/components/TableElements";
import { LinkSpan } from "../../../shared/components/form/FormElements";
import DataReactTable from "../../DataTable/components/DataReactTable";
import { useTranslation } from "react-i18next";
import ExplorePopup from "../../../shared/components/ExplorePopup";
import { NATEXT } from "../../../shared/constants";
import {
  formatNumber,
  getListingDisplayDate,
  getTranslatedReasonText,
} from "../../../shared/helpers";
import { useSelector, useDispatch } from "react-redux";
import getDefaultFilterData from "../../../shared/constants/getDefaultFilterData";
import useFetchData from "../../../shared/custom-hooks/useFetchData";
import getUnixTime from "date-fns/getUnixTime";
import subDays from "date-fns/subDays";
import startOfToday from "date-fns/startOfToday";
import endOfToday from "date-fns/endOfToday";
import { fetchPartCategories } from "../../../features/parts/partsSlice";

const UsageTable = () => {
  const { t } = useTranslation("common");
  const dispatch = useDispatch();
  const { currency } = useSelector((state) => state.units);
  const { minDate, maxDate, noOfRecords } = useSelector(
    (state) => state.columns
  );
  const rowData = useSelector((state) => state.filter.rowData);
  const optionValues = useSelector((state) => state.filter.values);
  const allVehicles = useSelector((state) => state.vehicles.list);
  const allPartCategories = useSelector(
    (state) => state.parts.allPartCategories
  );

  useFetchData({
    startDate: getUnixTime(subDays(startOfToday(), 89)), //0,
    endDate: getUnixTime(endOfToday()), //getUnixTime(new Date()),
    query: "get_all_usage_data",
    sortIndex: 3,
  });

  // Fetch part categories if not already loaded
  useEffect(() => {
    if (!allPartCategories || allPartCategories.length === 0) {
      dispatch(fetchPartCategories());
    }
  }, [dispatch, allPartCategories]);

  // const rowData = [
  //   {
  //     part_name: "Castrol Radicool",
  //     part_number: 12345,
  //     transaction_date: 1722579192,
  //     batch_id: "B-01",
  //     reason: 1,
  //     location: "",
  //     qty: 72,
  //     vendor: "Given Hardware",
  //     cost: 100,
  //     notes: "To be used with an oil and filter change",
  //     old_qty: 50,
  //     new_qty: 80,
  //     batch_available_qty: 65,
  //     veh_name: "Ford F150",
  //     purchase_cost: 1000,
  //     service_id: "21465_1703676646_1737960539773",
  //     wo_id: "",
  //   },
  //   {
  //     part_name: "Shell Engine Oil",
  //     part_number: 123456,
  //     transaction_date: 1722579192,
  //     batch_id: "B-02",
  //     reason: 1,
  //     location: "Waterworks",
  //     qty: 150,
  //     vendor: "Shell Outlet",
  //     cost: 250,
  //     notes: "To be used with an oil and filter change",
  //     old_qty: 80,
  //     new_qty: 40,
  //     batch_available_qty: 120,
  //     veh_name: "Ford F150",
  //     purchase_cost: 1000,
  //     service_id: "1",
  //     wo_id: "21465_1706591612_1738049091202",
  //     serviced_by_name: "Aaron",
  //     service_task_name: "New Service Task",
  //   },
  //   {
  //     part_name: "Shell Engine Oil",
  //     transaction_date: 1722579192,
  //     batch_id: "B-03",
  //     reason: 3,
  //     location: "Waterworks",
  //     qty: 150,
  //     vendor: "Shell Outlet",
  //     cost: 250,
  //     notes: "To be used with an oil and filter change",
  //     old_qty: 80,
  //     new_qty: 40,
  //     batch_available_qty: 120,
  //     veh_name: "Ford F150",
  //     purchase_cost: 1000,
  //     service_id: "",
  //     wo_id: "1",
  //     technician_name: "John",
  //     service_task_name: "New Service Task",
  //   },
  // ];

  const [showExplorePopup, setShowExplorePopup] = useState(false);

  const columnHeaders = useMemo(
    () => [
      {
        id: 1,
        Header: t("part_name"),
        accessor: "part_name",
        disableFilters: true,
      },
      {
        id: 2,
        Header: t("part_no"), //t("cost_per_unit"),
        accessor: "part_number",
        Cell: ({ value }) => value || NATEXT,
      },
      {
        id: 3,
        Header: t("date_tv"),
        accessor: "transaction_date",
        disableCol: true,
        Cell: ({ value }) => (value ? getListingDisplayDate(value) : NATEXT),
      },
      {
        id: 4,
        Header: t("vehicle"),
        accessor: "veh_id",
        Cell: ({ row }) => {
          const { veh_id } = row?.original;
          let vehicleName = NATEXT;
          if (veh_id) {
            const vehicle = allVehicles.find((v) => v.veh_id === veh_id);
            if (vehicle) {
              vehicleName = vehicle?.name;
            }
          }
          return vehicleName;
        },
      },
      {
        id: 5,
        Header: t("location"),
        accessor: "location",
        //disableCol: true,
      },
      {
        id: 6,
        Header: t("qty_change"),
        accessor: "qty_changed",
        Cell: ({ value }) => (value ? formatNumber(value) : NATEXT),
      },
      {
        id: 7,
        Header: t("reason"),
        accessor: "reason",
        disableSortBy: true,
        Cell: ({ value }) => getTranslatedReasonText(value, t),
      },
      {
        id: 8,
        Header: t("cost_per_unit"),
        accessor: "purchase_cost_per_unit",
        Cell: ({ value }) =>
          value ? `${formatNumber(value, 3)} ${currency}` : NATEXT,
      },
      {
        id: 9,
        Header: t("balance_qty"),
        accessor: "new_qty",
        Cell: ({ value }) => (value ? formatNumber(value) : NATEXT),
      },

      {
        id: 10,
        Header: t("batch_id"),
        accessor: "batch_id",
        //disableCol: true,
      },

      {
        id: 11,
        Header: t("vendor"),
        accessor: "vendor",
        Cell: ({ row }) => {
          const { reason, vendor } = row?.original;
          return vendor || NATEXT;
        },
      },
      {
        id: 12,
        Header: t("service_task"),
        accessor: "service_task_name",
        //width: columnWidth,
        accessorId: "task_name",
        Cell: ({ value }) => (value ? value : NATEXT),
        // disableGlobalFilter: false,
      },
      {
        id: 13,
        Header: t("part_category"),
        accessor: "category_name",
        Cell: ({ value }) =>
          value ? (
            <StatusContainer background={"#eff6ff"}>
              <p style={{ color: "#1447e6" }}>{value}</p>
            </StatusContainer>
          ) : (
            NATEXT
          ),
      },

      // {
      //   id: 13,
      //   Header: `${t("serviced_by")} (${t("technician")})`, //t("cost_per_unit"),
      //   accessor: "serviced_by",
      //   //Cell: ({ value }) => (value ? `${value} ${currency}` : NATEXT),
      // },
    ],
    [allVehicles]
  );

  //KPI Values
  let kpiValues = [
    {
      id: 0,
      key: "total_purchase_cost",
      name: t("total_purchase_cost"),
      value: optionValues?.total_purchase_cost
        ? `${formatNumber(optionValues.total_purchase_cost, 2)} ${currency}`
        : NATEXT,
    },
    {
      id: 1,
      key: "total_consumption_cost",
      name: t("total_consumption_cost"),
      value: optionValues?.total_consumption_cost
        ? `${formatNumber(optionValues.total_consumption_cost, 2)} ${currency}`
        : NATEXT,
    },
  ];

  const { defaultContainsData, defaultRangeData } = getDefaultFilterData();

  const initialData = useMemo(
    () => ({
      part_name: {
        column: "part_name",
        name: t("part_name"),
        ...defaultContainsData,
      },
      part_number: {
        column: "part_number",
        name: t("part_no"),
        ...defaultContainsData,
      },
      batch_id: {
        column: "batch_id",
        name: t("batch_id"),
        ...defaultContainsData,
      },
      location: {
        column: "location",
        name: t("location"),
        ...defaultContainsData,
      },
      qty_changed: {
        column: "qty_changed",
        name: t("qty_change"),
        ...defaultRangeData,
      },
      vendor: {
        column: "vendor",
        name: t("vendor"),
        ...defaultContainsData,
      },
      available_quantity: {
        column: "available_quantity",
        name: t("balance_qty"),
        ...defaultRangeData,
      },
      reason: {
        column: "reason",
        name: t("reason"),
        purchased: { header: t("purchased"), value: false },
        consumed: { header: t("consumed"), value: false },
        damaged: { header: t("damaged"), value: false },
        stolen: { header: t("stolen"), value: false },
        returned: { header: t("returned"), value: false },
        sold: { header: t("sold"), value: false },
        stockRecon: { header: t("stock_recon"), value: false },
        isFiltered: false,
        type: "checkbox",
      },
      // purchase_date: {
      //   column: "purchase_date",
      //   name: t("purchase_date"),
      //   dateOption: "all_time",
      //   from: new Date(minDate * 1000),
      //   to: new Date(),
      //   isFiltered: false,
      // },
      // store_room: {
      //   column: "store_room",
      //   name: t("store_room"),
      //   ...defaultContainsData,
      // },
      // custom_fields: {
      //   column: "custom_fields",
      //   name: t("custom_fields"),
      //   ...defaultContainsData,
      // },
    }),
    []
  );

  const reactTableData = {
    tableHeaderData: columnHeaders,
    tableRowsData: rowData,
  };

  return (
    <>
      {/* {showExplorePopup && (
        <ExplorePopup
          showPopup={showExplorePopup}
          setShowPopup={setShowExplorePopup}
          exploreLink={"https://embed.getsmartcue.com/IDIGFZHN"}
        />
      )} */}
      {/* {showPartsForm && (
        <PartsFormPopup
          showPopup={showPartsForm}
          setShowPopup={setShowPartsForm}
          mode={mode}
          rowData={singleRowData}
        />
      )}
      {showEditPart && (
        <EditPartPopup
          showPopup={showEditPart}
          setShowPopup={setShowEditPart}
          rowData={singleRowData}
        />
      )} */}
      <Container>
        <Row className="mx-1 d-flex align-items-center justify-content-around p-0 mt-1">
          <Col className="d-flex align-items-center gap-2">
            <ColumnHeading>{t("usage")}</ColumnHeading>
            {/* <LinkSpan
              onClick={() => setShowExplorePopup(true)}
              style={{ alignSelf: "end" }}
            >
              {t("explore")}
            </LinkSpan> */}
          </Col>
        </Row>
        <DataReactTable
          reactTableData={reactTableData}
          valuesArray={kpiValues}
          initialData={initialData}
          //defaultColumns={defaultColumns}
        />
      </Container>
    </>
    // <div style={{ height: '90%', background: 'lightblue' }}>Hi</div>
  );
};

export default UsageTable;
